import { useEffect, useState } from "react";
import { getFile, FileRecord } from "../../services/filesService";
import { loadSceneFromCloud } from "../../data/cloudScene";
import { ImportedDataState } from "../../../packages/excalidraw/data/types";
import { useAuth } from "../../hooks/useAuth";
import { isFileOwner } from "../../data/shareRouting";

/**
 * Data-loading for the read-only `/share/<id>` viewer. Deliberately split from
 * the chrome (`ShareView`) and the canvas (`ShareScene`) so the loader + canvas
 * can be reused chrome-less in the next slice's iframe/embed mode.
 *
 * This surface only ever GETs — there is no PUT path here, and no localStorage
 * buffer is written. Read-only is a structural property, not merely hidden UI.
 */

export type ShareFileStatus = "loading" | "ready" | "notfound";

export interface ShareFileState {
  status: ShareFileStatus;
  file: FileRecord | null;
  scene: ImportedDataState | null;
}

const NOT_FOUND: ShareFileState = {
  status: "notfound",
  file: null,
  scene: null,
};

/**
 * Pure data-load for a share URL: fetch the file row (RLS lets anyone read a
 * public row) and the scene (public-capable GET). A file is only "ready" when
 * BOTH the metadata row is readable AND the scene endpoint served it (200);
 * anything else collapses to `notfound` so private/deleted/nonexistent are
 * indistinguishable (no existence leak).
 */
export const loadShareFile = async (
  fileId: string,
  accessToken?: string,
): Promise<ShareFileState> => {
  const [file, sceneResult] = await Promise.all([
    getFile(fileId).catch(() => null),
    loadSceneFromCloud(fileId, accessToken),
  ]);

  if (!file || sceneResult.status !== 200 || !sceneResult.data) {
    return NOT_FOUND;
  }

  return { status: "ready", file, scene: sceneResult.data };
};

export interface UseShareFileResult extends ShareFileState {
  /** True only for a signed-in owner viewing their own file. */
  isOwner: boolean;
}

/**
 * React hook wrapper: gates on `authResolved` (so an owner's private file never
 * flashes 404 before the token arrives), loads the share file, and derives
 * owner-ness from the session vs the file's `user_id`.
 */
export const useShareFile = (fileId: string | null): UseShareFileResult => {
  const { session, authResolved } = useAuth();
  const [state, setState] = useState<ShareFileState>({
    status: "loading",
    file: null,
    scene: null,
  });

  useEffect(() => {
    if (!fileId) {
      setState(NOT_FOUND);
      return;
    }
    if (!authResolved) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading", file: null, scene: null });
    loadShareFile(fileId, session?.access_token).then((result) => {
      if (!cancelled) {
        setState(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fileId, authResolved, session?.access_token]);

  return {
    ...state,
    isOwner: isFileOwner(session?.user?.id, state.file),
  };
};
